import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, AlertTriangle, Eye, Share2, RefreshCw, Bug, ChevronDown, ChevronUp, QrCode, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL;

// Only enable debug in development
const DEBUG_ENABLED = process.env.NODE_ENV === 'development';

// Get base URL from browser
const getBaseUrl = () => window.location.origin;

export default function ViewerPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const viewerContainerRef = useRef(null);
  const viewerInstanceRef = useRef(null);
  const initAttemptedRef = useRef(false);
  
  const [modelInfo, setModelInfo] = useState(null);
  const [viewerToken, setViewerToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewerLoaded, setViewerLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [showQrModal, setShowQrModal] = useState(false);
  const [viewerReady, setViewerReady] = useState(false);
  
  // Debug panel - hidden by default, only available in dev mode
  const [showDebug, setShowDebug] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showWatermark, setShowWatermark] = useState(true);

  // Debug logging - sanitized, no sensitive data
  const addDebugLog = useCallback((type, message, data = null) => {
    if (!DEBUG_ENABLED) return;
    
    const log = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      type,
      message,
      data: data ? (typeof data === 'string' ? data : JSON.stringify(data, null, 2)) : null
    };
    setDebugLogs(prev => [log, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    addDebugLog('info', `Loading view: ${jobId}`);
    loadModelInfo();
    loadViewerSDK();
    
    // Fade out watermark after 5 seconds
    const watermarkTimer = setTimeout(() => {
      setShowWatermark(false);
    }, 5000);
    
    return () => {
      clearTimeout(watermarkTimer);
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.finish();
        viewerInstanceRef.current = null;
      }
    };
  }, [jobId]);

  const loadModelInfo = async () => {
    addDebugLog('request', 'Fetching model info');
    try {
      const response = await axios.get(`${API}/view/${jobId}`);
      // Don't log full response - only safe fields
      addDebugLog('success', 'Model info loaded', { 
        fileName: response.data.fileName,
        status: response.data.status 
      });
      setModelInfo(response.data);
      setLoadingProgress(30);
      setLoadingMessage('Model info loaded...');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Model not found';
      addDebugLog('error', 'Failed to load model info');
      setError(errorMsg);
      setLoading(false);
    }
  };

  const loadViewerToken = async () => {
    addDebugLog('request', 'Fetching viewer token');
    try {
      const response = await axios.get(`${API}/view/${jobId}/token`);
      // Never log tokens
      addDebugLog('success', 'Token received');
      setViewerToken(response.data.access_token);
      setLoadingProgress(50);
      setLoadingMessage('Token ready...');
    } catch (err) {
      addDebugLog('error', 'Failed to load token');
      setError('Failed to get viewer authentication');
      setLoading(false);
    }
  };

  const loadViewerSDK = () => {
    if (window.Autodesk && window.Autodesk.Viewing) {
      addDebugLog('success', 'SDK already loaded');
      setViewerLoaded(true);
      return;
    }

    addDebugLog('request', 'Loading Viewer SDK');
    setLoadingMessage('Loading Viewer SDK...');
    setLoadingProgress(10);

    const script = document.createElement('script');
    script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
    script.onload = () => {
      addDebugLog('success', 'SDK loaded');
      setViewerLoaded(true);
    };
    script.onerror = () => {
      addDebugLog('error', 'SDK load failed');
      setError('Failed to load Autodesk Viewer SDK');
      setLoading(false);
    };
    document.head.appendChild(script);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';
    document.head.appendChild(link);
  };

  useEffect(() => {
    if (viewerLoaded && modelInfo && !viewerToken) {
      loadViewerToken();
    }
  }, [viewerLoaded, modelInfo, viewerToken]);

  useEffect(() => {
    if (viewerLoaded && modelInfo && viewerToken && !initAttemptedRef.current) {
      const timer = setTimeout(() => initializeViewer(), 100);
      return () => clearTimeout(timer);
    }
  }, [viewerLoaded, modelInfo, viewerToken]);

  const initializeViewer = () => {
    if (initAttemptedRef.current) return;
    
    if (!viewerContainerRef.current) {
      addDebugLog('info', 'Container not ready, retrying...');
      setTimeout(initializeViewer, 200);
      return;
    }
    
    if (!window.Autodesk?.Viewing) return;

    initAttemptedRef.current = true;
    
    try {
      setLoadingMessage('Initializing viewer...');
      setLoadingProgress(60);

      const isAccModel = modelInfo.isAccModel;
      
      const options = {
        env: isAccModel ? 'AutodeskProduction2' : 'AutodeskProduction',
        api: isAccModel ? 'streamingV2' : 'derivativeV2',
        getAccessToken: (callback) => {
          callback(viewerToken, 3600);
        }
      };

      addDebugLog('info', 'Initializing viewer', { 
        env: options.env, 
        api: options.api 
      });

      window.Autodesk.Viewing.Initializer(options, () => {
        addDebugLog('success', 'Viewer initialized');
        
        if (!viewerContainerRef.current) return;
        
        const viewerInstance = new window.Autodesk.Viewing.GuiViewer3D(
          viewerContainerRef.current,
          { extensions: ['Autodesk.DocumentBrowser'] }
        );

        const startCode = viewerInstance.start();
        
        if (startCode > 0) {
          addDebugLog('error', `Viewer start failed: ${startCode}`);
          setError(`Failed to start viewer`);
          setLoading(false);
          return;
        }

        viewerInstanceRef.current = viewerInstance;
        setLoadingMessage('Loading document...');
        setLoadingProgress(75);

        const documentId = `urn:${modelInfo.urn}`;
        addDebugLog('info', 'Loading document');
        
        window.Autodesk.Viewing.Document.load(
          documentId,
          (doc) => onDocumentLoadSuccess(doc, viewerInstance),
          onDocumentLoadFailure
        );
      });
    } catch (err) {
      addDebugLog('error', 'Viewer exception');
      setError(`Viewer error: ${err.message}`);
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = (doc, viewerInstance) => {
    addDebugLog('success', 'Document loaded');
    
    const viewables = doc.getRoot().getDefaultGeometry();
    
    if (!viewables) {
      addDebugLog('error', 'No geometry found');
      setError('No viewable geometry found. Ensure the model is PUBLISHED in ACC.');
      setLoading(false);
      return;
    }

    addDebugLog('info', 'Loading geometry');
    setLoadingMessage('Loading 3D geometry...');
    setLoadingProgress(90);

    viewerInstance.loadDocumentNode(doc, viewables)
      .then(() => {
        addDebugLog('success', 'Model loaded');
        setLoadingProgress(100);
        setLoading(false);
        setViewerReady(true);
        toast.success('3D model loaded');
      })
      .catch((err) => {
        addDebugLog('error', 'Load failed');
        setError(`Failed to load model: ${err.message || err}`);
        setLoading(false);
      });
  };

  const onDocumentLoadFailure = (errorCode, errorMsg, messages) => {
    addDebugLog('error', `Document load failed: ${errorCode}`);
    
    let detail = `Load failed (code ${errorCode})`;
    if (errorMsg) detail += `: ${errorMsg}`;
    
    const hints = {
      4: 'No viewable derivatives. Model must be PUBLISHED in ACC.',
      5: 'Network error or invalid URN.',
      6: 'Access denied.',
      7: 'File not found.',
      9: 'Timeout loading model.'
    };
    if (hints[errorCode]) detail += `\n\n${hints[errorCode]}`;
    
    setError(detail);
    setLoading(false);
  };

  const shareUrl = () => {
    const currentUrl = `${getBaseUrl()}/view/${jobId}`;
    navigator.clipboard.writeText(currentUrl);
    toast.success('URL copied');
  };

  const copyUrl = () => {
    const currentUrl = `${getBaseUrl()}/view/${jobId}`;
    navigator.clipboard.writeText(currentUrl);
    toast.success('URL copied');
  };

  const retry = () => {
    setError('');
    setLoading(true);
    setLoadingProgress(0);
    setModelInfo(null);
    setViewerToken(null);
    setViewerReady(false);
    initAttemptedRef.current = false;
    if (viewerInstanceRef.current) {
      viewerInstanceRef.current.finish();
      viewerInstanceRef.current = null;
    }
    loadModelInfo();
  };

  // Debug Panel - only in dev mode
  const DebugPanel = () => {
    if (!DEBUG_ENABLED) return null;
    
    return (
      <div className={`fixed bottom-0 left-0 right-0 bg-slate-900 text-white z-50 transition-all ${showDebug ? 'h-48' : 'h-7'}`}>
        <div 
          className="flex items-center justify-between px-3 py-1 bg-slate-800 cursor-pointer border-t border-slate-700"
          onClick={() => setShowDebug(!showDebug)}
        >
          <div className="flex items-center gap-2">
            <Bug className="w-3 h-3 text-yellow-400" />
            <span className="font-mono text-xs text-yellow-400">DEV DEBUG</span>
            <Badge className="bg-slate-700 text-xs py-0">{debugLogs.length}</Badge>
          </div>
          {showDebug ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        </div>
        
        {showDebug && (
          <div className="h-40 overflow-y-auto p-2 font-mono text-xs">
            {debugLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-2 py-0.5">
                <span className={`px-1 rounded text-xs ${
                  log.type === 'error' ? 'bg-red-600' :
                  log.type === 'success' ? 'bg-green-600' :
                  log.type === 'request' ? 'bg-blue-600' : 'bg-slate-600'
                }`}>{log.type[0].toUpperCase()}</span>
                <span className="text-slate-400">{new Date(log.timestamp).toLocaleTimeString()}</span>
                <span className="text-slate-200">{log.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Error view
  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="card w-full max-w-md">
          <CardHeader className="text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
            <CardTitle className="technical-header text-red-700 text-lg">MODEL UNAVAILABLE</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription className="text-sm whitespace-pre-wrap">{error}</AlertDescription>
            </Alert>
            
            {modelInfo && (
              <div className="p-2 bg-slate-100 rounded text-xs space-y-1">
                <div><strong>File:</strong> {modelInfo.fileName}</div>
              </div>
            )}
            
            <div className="flex gap-2 justify-center">
              <Button onClick={retry} className="btn-secondary" data-testid="retry-button">
                <RefreshCw className="w-4 h-4 mr-2" />RETRY
              </Button>
              <Button onClick={() => navigate('/')} className="btn-primary" data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" />DASHBOARD
              </Button>
            </div>
          </CardContent>
        </Card>
        <DebugPanel />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="model-viewer">
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 z-40">
          <Card className="card w-80">
            <CardHeader className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 relative">
                <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
                <Eye className="w-4 h-4 text-orange-500 absolute inset-0 m-auto" />
              </div>
              <CardTitle className="technical-header text-base">LOADING MODEL</CardTitle>
              <CardDescription className="technical-subtext text-sm">{loadingMessage}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="progress-container">
                  <div className="progress-fill" style={{ width: `${loadingProgress}%` }}></div>
                </div>
                <div className="text-center text-sm font-mono text-slate-600">{loadingProgress}%</div>
                {modelInfo && (
                  <div className="text-center text-xs text-slate-500 truncate">{modelInfo.fileName}</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Viewer container */}
      <div 
        ref={viewerContainerRef} 
        className="w-full h-screen"
        data-testid="viewer-container"
      />

      {/* Controls */}
      {viewerReady && (
        <>
          <div className="absolute top-4 right-4 z-30">
            <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm p-2 shadow-lg">
              <Badge className="badge-success">
                <Eye className="w-3 h-3 mr-1" />LIVE
              </Badge>
              <span className="text-sm font-medium text-slate-800 max-w-32 truncate">{modelInfo?.fileName}</span>
              <Button size="sm" onClick={() => setShowQrModal(true)} className="btn-ghost h-7 px-2" title="Show QR Code">
                <QrCode className="w-3 h-3" />
              </Button>
              <Button size="sm" onClick={shareUrl} className="btn-ghost h-7 px-2">
                <Share2 className="w-3 h-3 mr-1" />SHARE
              </Button>
              <Button size="sm" onClick={() => navigate('/')} className="btn-secondary h-7 px-2">
                <ArrowLeft className="w-3 h-3 mr-1" />BACK
              </Button>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 z-30 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm p-3 shadow-lg max-w-xs">
            <h3 className="font-bold text-slate-800 mb-2 text-xs technical-header">MODEL INFO</h3>
            <div className="space-y-1 text-xs">
              <div><span className="text-slate-500">File:</span> <span className="text-slate-800">{modelInfo?.fileName}</span></div>
              <div><span className="text-slate-500">Source:</span> <Badge className="badge-success text-xs ml-1">ACC</Badge></div>
            </div>
          </div>
        </>
      )}

      {/* QR Code Modal */}
      {showQrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQrModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="technical-header text-lg">SCAN TO VIEW</h3>
              <button 
                onClick={() => setShowQrModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex justify-center mb-4 p-4 bg-white border border-slate-200 rounded-lg">
              <QRCodeSVG 
                value={`${getBaseUrl()}/view/${jobId}`} 
                size={200}
                level="M"
                includeMargin={true}
              />
            </div>
            
            <div className="text-center mb-4">
              <p className="text-xs text-slate-500 mb-2">Scan this QR code to open the 3D model viewer</p>
              <p className="mono text-xs text-blue-600 break-all bg-slate-50 p-2 rounded">
                {`${getBaseUrl()}/view/${jobId}`}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={copyUrl}
                className="btn-secondary flex-1 text-xs"
              >
                <Copy className="w-3 h-3 mr-2" />
                COPY URL
              </Button>
              <Button 
                onClick={() => setShowQrModal(false)}
                className="btn-primary flex-1 text-xs"
              >
                CLOSE
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Subtle watermark - fades after 5s */}
      <div 
        className={`fixed bottom-4 right-4 z-10 transition-opacity duration-1000 ${
          showWatermark ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-sm px-3 py-1.5 shadow-sm">
          <p className="text-xs text-slate-500 font-light">
            Built by{' '}
            <a 
              href="https://thedevguys.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium"
            >
              The Dev Guys
            </a>
          </p>
        </div>
      </div>

      <DebugPanel />
    </div>
  );
}
